import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  SeguimientosDesplazamientos,
  SeguimientosDesplazamientosPayload,
  SeguimientosDesplazamientosService,
} from "../../../../../shared/services/seguimientos-desplazamientos.service";
import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";
import { EstadoConductorService } from "../../../../../shared/services/estado-conductor.service";

@Component({
  selector: "app-seguimientos-desplazamientos",
  templateUrl: "./seguimientos-desplazamientos.component.html",
  styleUrls: ["./seguimientos-desplazamientos.component.scss"],
})
export class SeguimientosDesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  seguimientoForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idSeguimientoEditando: number | null = null;

  // Catálogo (estado_conductor es numérico en el backend)
  estadosConductor: any[] = [];

  // Listado
  seguimientos: SeguimientosDesplazamientos[] = [];
  seguimientosFiltrados: SeguimientosDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private seguimientosService: SeguimientosDesplazamientosService,
    private desplazamientosService: PesvDesplazamientosService,
    private estadoConductorService: EstadoConductorService,
  ) {}

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarSeguimientos();
    this.cargarEstadoConductor();
  }

  private inicializarFormulario(): void {
    this.seguimientoForm = this.fb.group({
      fecha: ["", Validators.required],
      ubicacion: [""],
      kilometraje: [null, [Validators.min(0)]],
      velocidad: [null, [Validators.min(0)]],
      estado_conductor: [null, Validators.required],
      novedad: [""],
      accion_tomada: [""],
      observaciones: [""],
    });
  }
  
  private cargarEstadoConductor(): void {
    this.estadoConductorService.getEstados().subscribe({
      next: (data: any) => {
        this.estadosConductor = this.normalizarLista(data);
      },
      error: () => {
        this.estadosConductor = [];
      },
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ Helpers de presentación ============
  nombreEstadoConductor(id?: number | null): string {
    if (id === null || id === undefined) return "-";
    return this.estadosConductor.find((e) => e.id === Number(id))?.descripcion || "-";
  }

  claseEstadoConductor(id?: number | null): string {
    switch (Number(id)) {
      case 1:
        return "bg-success";
      case 2:
      case 4:
        return "bg-warning text-dark";
      case 3:
      case 5:
        return "bg-danger";
      case 6:
        return "bg-dark";
      default:
        return "bg-secondary";
    }
  }

  // ============ Listado ============
  cargarSeguimientos(): void {
    this.cargandoListado = true;
    this.seguimientosService.getByDesplazamiento(this.idDesplazamiento).subscribe({
      next: (data: any) => {
        this.seguimientos = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.seguimientos = [];
        this.seguimientosFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.seguimientosFiltrados = this.seguimientos.filter((s) => {
      const coincideTexto =
        !termino ||
        [
          s.ubicacion,
          s.novedad,
          s.accion_tomada,
          s.observaciones,
          this.nombreEstadoConductor(s.estado_conductor),
        ]
          .filter((v) => v && v !== "-")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || s.estado === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aNumeroONull(valor: any): number | null {
    return valor === null || valor === undefined || valor === "" ? null : Number(valor);
  }

  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): SeguimientosDesplazamientosPayload {
    const raw = this.seguimientoForm.getRawValue();
    return {
      desplazamiento_id: this.idDesplazamiento,
      fecha: raw.fecha,
      ubicacion: this.aTextoONull(raw.ubicacion),
      kilometraje: this.aNumeroONull(raw.kilometraje),
      velocidad: this.aNumeroONull(raw.velocidad),
      estado_conductor: Number(raw.estado_conductor),
      novedad: this.aTextoONull(raw.novedad),
      accion_tomada: this.aTextoONull(raw.accion_tomada),
      observaciones: this.aTextoONull(raw.observaciones),
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear seguimientos para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.seguimientoForm.invalid) {
      this.seguimientoForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idSeguimientoEditando
        ? this.seguimientosService.update(this.idSeguimientoEditando, payload)
        : this.seguimientosService.create({ ...payload, estado: 1 });

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Seguimiento guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarSeguimientos();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el seguimiento.",
          "error",
        );
      },
    });
  }

  editar(seguimiento: SeguimientosDesplazamientos): void {
    if (Number(seguimiento.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar seguimientos activos.", "warning");
      return;
    }

    if (!seguimiento.id) return;

    this.seguimientosService.getById(seguimiento.id).subscribe({
      next: (respuesta: any) => {
        const detalle = respuesta?.data ?? respuesta;
        this.modoEdicion = true;
        this.idSeguimientoEditando = detalle.id ?? seguimiento.id;
        this.seguimientoForm.patchValue({
          fecha: detalle.fecha?.substring(0, 10),
          ubicacion: detalle.ubicacion ?? "",
          kilometraje: detalle.kilometraje ?? null,
          velocidad: detalle.velocidad ?? null,
          estado_conductor: detalle.estado_conductor ?? null,
          novedad: detalle.novedad ?? "",
          accion_tomada: detalle.accion_tomada ?? "",
          observaciones: detalle.observaciones ?? "",
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudo cargar el seguimiento para editar.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idSeguimientoEditando = null;
    this.seguimientoForm.reset();
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Activar / Inactivar ============
  inactivar(seguimiento: SeguimientosDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar seguimiento?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !seguimiento.id) return;
      this.seguimientosService.inactivar(seguimiento.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Seguimiento inactivado.", "success");
          this.cargarSeguimientos();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el seguimiento.", "error"),
      });
    });
  }

  activar(seguimiento: SeguimientosDesplazamientos): void {
    Swal.fire({
      title: "¿Activar seguimiento?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !seguimiento.id) return;
      this.seguimientosService.activar(seguimiento.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Seguimiento activado.", "success");
          this.cargarSeguimientos();
        },
        error: () => Swal.fire("Error", "No se pudo activar el seguimiento.", "error"),
      });
    });
  }

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}